import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import PageLayout from './shared/components/layout/PageLayout'

// Feature-based imports
import { DashboardPage } from './features/dashboard'
import { OpenTendersPage } from './features/open-tenders'
import { TendersPage, TenderDetailPage } from './features/tenders'
import { OrganizationsPage, OrganizationDetailPage } from './features/organizations'
import {
  ContractorsPage,
  ContractorDetailPage,
  RelationshipDrillDown,
} from './features/contractors'
import { StatisticsPanel } from './features/dashboard'
import { LoginPage, AuthProvider, ProtectedRoute, useAuthContext } from './features/auth'
import LandingPage from './features/landing/LandingPage'

function LegacyStatisticsOrganizationRedirect() {
  const { organizationName } = useParams()
  const location = useLocation()
  return <Navigate replace to={`/organizations/${organizationName}${location.search}`} />
}

function LegacyStatisticsContractorRedirect() {
  const { contractorUuid } = useParams()
  const location = useLocation()
  return <Navigate replace to={`/contractors/${contractorUuid}${location.search}`} />
}

function LegacyStatisticsRelationshipRedirect() {
  const { organizationName, contractorUuid } = useParams()
  const location = useLocation()
  return (
    <Navigate
      replace
      to={`/contractors/relationship/${organizationName}/${contractorUuid}${location.search}`}
    />
  )
}

/**
 * PublicOnly — renders children only if NOT authenticated.
 * Redirects to "/" if already logged in.
 */
function PublicOnly({ children }) {
  const { isAuthenticated, isLoading } = useAuthContext()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-slate-400">Weryfikacja sesji...</div>
      </div>
    )
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/**
 * AuthGate — landing page for anonymous, dashboard for authenticated.
 */
function AuthGate() {
  const { isAuthenticated, isLoading } = useAuthContext()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-slate-400">Weryfikacja sesji...</div>
      </div>
    )
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <LandingPage />
}

/**
 * AppLayout — wraps authenticated routes with PageLayout (top menu bar).
 * Only renders inside ProtectedRoute, so user is always authenticated.
 */
function AppLayout() {
  return (
    <PageLayout>
      <Outlet />
    </PageLayout>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public: landing page */}
          <Route path="/" element={<AuthGate />} />

          {/* Public: login (no menu, no app chrome) */}
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />

          {/* Protected app routes — PageLayout visible ONLY here */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/tenders" element={<TendersPage />} />
            <Route path="/tenders/:id" element={<TenderDetailPage />} />
            <Route path="/open-tenders" element={<OpenTendersPage />} />
            <Route path="/organizations" element={<OrganizationsPage />} />
            <Route path="/organizations/:organizationName" element={<OrganizationDetailPage />} />
            <Route path="/contractors" element={<ContractorsPage />} />
            <Route path="/contractors/:contractorUuid" element={<ContractorDetailPage />} />
            <Route
              path="/contractors/relationship/:organizationName/:contractorUuid"
              element={<RelationshipDrillDown />}
            />

            {/* Statistics */}
            <Route path="/statistics/notices" element={<Navigate replace to="/statistics" />} />
            <Route path="/statistics/results" element={<Navigate replace to="/statistics" />} />
            <Route
              path="/statistics/organization/:organizationName"
              element={<LegacyStatisticsOrganizationRedirect />}
            />
            <Route
              path="/statistics/contractor/:contractorUuid"
              element={<LegacyStatisticsContractorRedirect />}
            />
            <Route
              path="/statistics/relationship/:organizationName/:contractorUuid"
              element={<LegacyStatisticsRelationshipRedirect />}
            />
            <Route path="/statistics/*" element={<StatisticsPanel />} />

            {/* Legacy routes */}
            <Route path="/notices" element={<TendersPage />} />
            <Route path="/notices/:id" element={<TenderDetailPage />} />
          </Route>

          {/* Catch all — landing for anonymous, dashboard for authenticated */}
          <Route path="*" element={<AuthGate />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
